/**
 * "New prompt" modal — list page's "+ New prompt" button. Captures
 * name + type + optional description + optional tags + initial body
 * + commit message. The POST hits a single endpoint that creates the
 * Prompt row, seeds v1 with the supplied body + message, AND
 * activates v1 — see `routes/admin/prompts.ts` POST `/`. On success
 * the parent navigates to the new prompt's detail page.
 */
import { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/ui'
import { adminPromptsAPI, ADMIN_PROMPT_TYPES, type AdminPromptType } from '@/lib/api'

const TYPE_LABEL: Record<AdminPromptType, string> = {
  planner: 'Planner', generator: 'Generator', healer: 'Healer',
  analyzer: 'Analyzer', auditor: 'Auditor', scanner: 'Scanner',
  proposer: 'Proposer', refiner: 'Refiner', dedupJudge: 'Deduper', shared: 'Shared rules',
}

export interface NewPromptDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (promptId: string) => void
}

export function NewPromptDialog({ open, onOpenChange, onCreated }: NewPromptDialogProps) {
  const [name, setName] = useState('')
  const [type, setType] = useState<AdminPromptType>('planner')
  const [description, setDescription] = useState('')
  const [tagsInput, setTagsInput] = useState('')
  const [body, setBody] = useState('')
  const [commitMessage, setCommitMessage] = useState('initial seed')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset every open so a re-open after success doesn't show stale state.
  useEffect(() => {
    if (open) {
      setName('')
      setType('planner')
      setDescription('')
      setTagsInput('')
      setBody('')
      setCommitMessage('initial seed')
      setError(null)
    }
  }, [open])

  const tags = tagsInput
    .split(',')
    .map(t => t.trim())
    .filter(Boolean)

  const canSubmit =
    name.trim().length > 0 &&
    body.trim().length > 0 &&
    commitMessage.trim().length > 0 &&
    !submitting

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const out = await adminPromptsAPI.create({
        name: name.trim(),
        type,
        description: description.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
        initialBody: body,
        commitMessage: commitMessage.trim(),
      })
      onCreated(out.prompt._id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create prompt')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New prompt</DialogTitle>
          <DialogDescription>Creates the prompt + seeds v1 + activates it.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="prompt-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="prompt-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. planner.system"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="prompt-type">
                Type <span className="text-destructive">*</span>
              </Label>
              <select
                id="prompt-type"
                value={type}
                onChange={(e) => setType(e.target.value as AdminPromptType)}
                className="w-full bg-background border border-border rounded-md px-3 py-1.5 text-sm"
              >
                {ADMIN_PROMPT_TYPES.map(t => (
                  <option key={t} value={t}>{TYPE_LABEL[t]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="prompt-description">Description (optional)</Label>
            <Input
              id="prompt-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this prompt is for"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="prompt-tags">Tags (comma-separated, optional)</Label>
            <Input
              id="prompt-tags"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="experimental, wave-3"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="prompt-body">
              Body (v1) <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="prompt-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="The initial prompt text..."
              rows={8}
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="prompt-commit">
              Commit message <span className="text-destructive">*</span>
            </Label>
            <Input
              id="prompt-commit"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" role="alert">{error}</p>
          )}
        </DialogBody>
        <DialogFooter>
          <span className="mr-auto text-xs text-muted-foreground self-center">* required</span>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create + publish v1'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
